import React, { useState } from "react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { FadeUp, Eyebrow } from "@/components/Motion";
import PageHero from "@/components/PageHero";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSeo } from "@/lib/useSeo";

const FAQ = [
  ["Do I need to book in advance to visit the gallery?", "Walk-ins are welcome during open hours, though we recommend reserving for weekend afternoons when the studio is at its most vibrant."],
  ["What ages are the programs designed for?", "Our four-week experiences are crafted for children ages 3–5. Family gallery hours are open to all ages."],
  ["Is photography allowed?", "Yes — we love when wonder is shared. We ask only that flash is turned off near original artworks."],
  ["Can I purchase artwork seen in the gallery?", "Many pieces are available through our Gallery Shop. For commissions or acquisitions, please reach out via the contact form."],
];

export default function Visit() {
  const { c } = useSiteContent();
  useSeo(c("seo_visit_title"), c("seo_visit_desc"));
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [email, setEmail] = useState("");

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", contact);
      toast.success("Message sent. We'll be in touch soon.");
      setContact({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the list.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  const socials = [
    ["visit_instagram", "Instagram", Instagram, "social-instagram"],
    ["visit_facebook", "Facebook", Facebook, "social-facebook"],
    ["visit_youtube", "Youtube", Youtube, "social-youtube"],
  ];

  return (
    <div data-testid="visit-page">
      <PageHero
        eyebrow={c("visit_hero_subtitle", "Visit")}
        title={c("visit_hero_title", "Come wander, wonder, and stay a while.")}
        image=""
      />

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-4 space-y-12">
          {c("visit_hours_value") && (
            <FadeUp>
              <Eyebrow>{c("visit_hours_label", "Hours")}</Eyebrow>
              <ul className="mt-4 space-y-2 text-matte/70">
                <li className="flex justify-between border-b border-matte/10 pb-2"><span>Open</span><span>{c("visit_hours_value")}</span></li>
              </ul>
            </FadeUp>
          )}
          {c("visit_location_text") && (
            <FadeUp delay={0.1}>
              <Eyebrow>{c("visit_location_label", "Location")}</Eyebrow>
              <p className="mt-4 text-matte/70 leading-relaxed whitespace-pre-line">
                {c("visit_location_text")}
              </p>
            </FadeUp>
          )}
          {c("visit_phone") && (
            <FadeUp delay={0.12}>
              <Eyebrow>{c("visit_phone_label", "Phone")}</Eyebrow>
              <p className="mt-4 text-matte/70 leading-relaxed">
                <a href={`tel:${c("visit_phone").replace(/[^0-9+]/g, "")}`} className="link-underline" data-testid="visit-phone">{c("visit_phone")}</a>
              </p>
            </FadeUp>
          )}
          <FadeUp delay={0.15}>
            <Eyebrow>{c("visit_connect_label", "Connect")}</Eyebrow>
            <div className="mt-4 flex gap-5 text-matte/70">
              {socials.map(([key, label, Icon, tid]) => (
                <a key={key} href={c(key) || "#"} target={c(key) ? "_blank" : undefined} rel="noreferrer" aria-label={label} className="hover:text-plum transition-colors" data-testid={tid}>
                  <Icon size={20} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="md:col-span-8">
          <FadeUp>
            <Eyebrow>{c("visit_contact_eyebrow", "Contact")}</Eyebrow>
            <form onSubmit={submitContact} className="mt-6 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-5">
                <input
                  required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  placeholder="Your name" data-testid="contact-name"
                  className="bg-transparent border-b border-matte/20 py-3 focus:outline-none focus:border-matte transition-colors"
                />
                <input
                  required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="Email" data-testid="contact-email"
                  className="bg-transparent border-b border-matte/20 py-3 focus:outline-none focus:border-matte transition-colors"
                />
              </div>
              <textarea
                required rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
                placeholder="Your message" data-testid="contact-message"
                className="w-full bg-transparent border-b border-matte/20 py-3 focus:outline-none focus:border-matte transition-colors resize-none"
              />
              <button
                type="submit" data-testid="contact-submit"
                className="bg-matte text-ivory px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
              >
                Send Message
              </button>
            </form>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <Eyebrow>Questions</Eyebrow>
        <h2 className="font-serif text-4xl md:text-5xl font-light mt-3 mb-10">{c("visit_faq_title", "Frequently Asked")}</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`} className="border-b border-matte/15">
              <AccordionTrigger className="font-serif text-xl md:text-2xl text-left hover:no-underline py-6" data-testid={`faq-${i}`}>
                {q}
              </AccordionTrigger>
              <AccordionContent className="text-matte/60 leading-relaxed text-base pb-6">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-matte text-ivory">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <Eyebrow className="!text-brass">Newsletter</Eyebrow>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 mb-8">
            {c("visit_newsletter_title", "Stay close to the wonder")}
          </h2>
          <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address" data-testid="newsletter-email"
              className="flex-1 bg-transparent border-b border-ivory/30 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory transition-colors"
            />
            <button
              type="submit" data-testid="newsletter-submit"
              className="bg-ivory text-matte px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-cream transition-colors duration-300"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
